import { AddCircleOutlineOutlined } from '@mui/icons-material';
import {
  CollapseActionButton,
  TooltipType,
} from 'pages/Asset/tabs/Cases/components/Collapse/CollapseTitle';
import { useEffect, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FilesList } from 'types/filemanager.contract';
import { useSaveEvidences } from './useSaveEvidences';
import { useModal } from '@shape-ds/components';
import { EvidenceTabsWithFormContext } from '../components/EvidenceContent/EvidenceTabsWithFormContext';
import { useFeatureFlag } from '@shape/feature-flag';
import { useOilAnalysis } from './useOilAnalysis';
import { useVibrationAnalysis } from './useVibrationAnalysis';
import { useFormContext } from 'react-hook-form';
import { CaseForm, FileEvidences } from 'queries/cases/types';

interface HookReturn {
  name: string;
  label: string;
  title: string;
  attachments: number;
  tooltip: TooltipType;
  button: CollapseActionButton;
  fileInputRef: React.RefObject<HTMLInputElement>;
  filesData?: FilesList;
  isLoading: boolean;
  disabled: boolean;
}

export const useEvidencesHook = (): HookReturn => {
  const isModalEnabled = useFeatureFlag('attachments-modal');
  const { openModal } = useModal();
  const { t } = useTranslation();
  const formMethods = useFormContext<CaseForm>();
  const caseId = useWatch({ name: 'id' });
  const asset = useWatch({ name: 'asset' });

  const formEvidences = useWatch({ name: 'evidences' }) || [];
  const visibleFormEvidences = formEvidences.filter(
    (evidence: FileEvidences) => !evidence.isDeleted,
  );

  const { attachments, filesData, isLoading } = useSaveEvidences({
    eventId: caseId,
  });
  const { editedOilAnalysisData } = useOilAnalysis(asset?.id ?? '');
  const { editedVibrationAnalysisData } = useVibrationAnalysis(asset?.id ?? '');

  const [totalAttachments, setTotalAttachments] = useState<number>(0);

  useEffect(() => {
    let total = Math.max(attachments, visibleFormEvidences.length);

    if (editedOilAnalysisData && editedOilAnalysisData.length > 0) {
      total += 1;
    }

    if (editedVibrationAnalysisData && editedVibrationAnalysisData.length > 0) {
      total += 1;
    }

    setTotalAttachments(Math.max(0, total));
  }, [
    editedOilAnalysisData,
    editedVibrationAnalysisData,
    attachments,
    filesData,
    visibleFormEvidences.length,
  ]);

  const name = 'evidences';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = () => {
    if (isModalEnabled) {
      const currentTotal =
        Math.max(attachments, visibleFormEvidences.length) +
        (editedOilAnalysisData && editedOilAnalysisData.length > 0 ? 1 : 0) +
        (editedVibrationAnalysisData && editedVibrationAnalysisData.length > 0 ? 1 : 0);

      openModal(
        <EvidenceTabsWithFormContext
          assetId={asset?.id ?? ''}
          caseId={caseId ?? ''}
          formMethods={formMethods}
        />,
        {
          title: t('case:evidences.title', { quantity: currentTotal }),
          className: 'evidences-modal',
        },
      );
    } else {
      fileInputRef.current?.click();
    }
  };

  return {
    name,
    attachments: totalAttachments,
    fileInputRef,
    filesData,
    label: t(`case:tabs.${name}`, { quantity: totalAttachments }),
    title: t('case:evidences.title', { quantity: totalAttachments }),
    button: {
      text: t('case:evidences.attach'),
      onClick: () => {
        handleClick();
      },
      icon: <AddCircleOutlineOutlined />,
    },
    tooltip: { title: t('case:evidences.messages.tooltip') },
    isLoading,
    disabled: isLoading,
  };
};
